import React, { Fragment, useState, useRef } from 'react';
import styled from 'styled-components';
import { useDrop } from 'react-dnd';
import { Flex } from '../../../ui';
import { media } from '../../../utils/media';
import { HorizontalDropZone } from './DropZone';
import { ItemTypes } from '../utils/dragTypes';

const RowModules = styled(Flex)`
  ${media.lessThan('laptop')} {
    flex-direction: ${({ $isNotesOpen }) => $isNotesOpen && 'column'};
  }
  ${media.lessThan('bigTablet')} {
    flex-direction: column;
  }

  //hide empty components so we dont have extra empty space
  &:empty {
    display: none;
  }
`;

const ModuleGridContainer = styled.div`
  display: inline-grid;
  grid-template-columns: ${({ $columns }) => ($columns === 3 ? '32% 32% 32%' : '49% 49%')};
  overflow: auto;
  width: 100%;
  justify-content: space-between;
  align-items: start;
  position: relative;
  grid-row-gap: 25px;

  ${media.lessThan('bigTablet')} {
    grid-template-columns: repeat(1, 98%);
    grid-gap: 25px;
  }
`;

const RowWrapper = styled.div`
  position: relative;
  width: 100%;
  // z-index: 2;
`;

const DropZoneContainer = styled.div`
  display: ${({ $isActive }) => ($isActive ? 'flex' : 'none')};
  align-items: center;
  justify-content: ${({ $justify }) => $justify || 'flex-start'};
  width: 100%;
  gap: 0;
`;

/**
 * DraggableRow - Enhanced Row component with horizontal drop zones for drag-and-drop
 *
 * @param {Object} props
 * @param {Object} props.pageRow - The page row data containing modules
 * @param {boolean} props.isNotesOpen - Whether notes panel is open (affects layout on mobile)
 * @param {Function} props.renderModule - Function to render individual modules
 * @param {Object} props.moduleProps - Additional props to pass to each module
 * @param {boolean} props.isDragging - Whether a module is currently being dragged
 * @param {Object} props.draggedModule - The module currently being dragged
 * @param {Function} props.onDrop - Callback when a module is dropped
 * @param {string} props.themeColor - Theme color for drop zones
 * @param {Function} props.onBottomEdgeHover - Callback when bottom edge trigger zone is hovered
 * @returns {JSX.Element}
 */
const DraggableRow = ({
  pageRow,
  isNotesOpen,
  renderModule,
  moduleProps = {},
  isDragging = false,
  draggedModule = null,
  onDrop,
  themeColor,
  onTopHalfHover, // Callback when hovering top half of row
  onBottomHalfHover, // Callback when hovering bottom half of row
  verticalOnly = false, // When true, disable horizontal drop zones (for quiz pages)
}) => {
  const [isTopHalfHovered, setIsTopHalfHovered] = useState(false);
  const [isBottomHalfHovered, setIsBottomHalfHovered] = useState(false);
  const rowRef = useRef(null);

  const pageModulesLength = pageRow.pageModules.length;
  const ModuleContainer = pageModulesLength < 2 ? Fragment : ModuleGridContainer;
  const containerProps = pageModulesLength > 1 ? { $columns: pageModulesLength } : {};

  // Check if the dragged module is in this row
  const draggedModuleInThisRow = draggedModule && pageRow.pageModules.some((m) => m.id === draggedModule.id);

  // Use useDrop hook for touch compatibility (instead of HTML5 drag events)
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.MODULE,
      hover: (_item, monitor) => {
        if (!rowRef.current) return;

        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;

        const rect = rowRef.current.getBoundingClientRect();
        const mouseY = clientOffset.y;
        const rowTop = rect.top;
        const rowHeight = rect.height;
        const rowMiddle = rowTop + rowHeight / 2;

        // Determine if we're in top half or bottom half
        const isInTopHalf = mouseY < rowMiddle;
        const isInBottomHalf = mouseY >= rowMiddle;

        // Top half detection
        if (onTopHalfHover && isInTopHalf !== isTopHalfHovered) {
          setIsTopHalfHovered(isInTopHalf);
          onTopHalfHover(isInTopHalf);
        }

        // Bottom half detection
        if (onBottomHalfHover && isInBottomHalf !== isBottomHalfHovered) {
          setIsBottomHalfHovered(isInBottomHalf);
          onBottomHalfHover(isInBottomHalf);
        }
      },
      collect: (monitor) => {
        // Use isOver() without shallow to keep drop zones visible when hovering over them
        return { isOver: monitor.isOver() };
      },
    }),
    [onTopHalfHover, onBottomHalfHover, isTopHalfHovered, isBottomHalfHovered, pageRow.id],
  );

  // Reset hover state when not hovering
  React.useEffect(() => {
    if (!isOver) {
      setIsTopHalfHovered(false);
      setIsBottomHalfHovered(false);
      if (onTopHalfHover) onTopHalfHover(false);
      if (onBottomHalfHover) onBottomHalfHover(false);
    }
  }, [isOver, onTopHalfHover, onBottomHalfHover, pageRow.id]);

  // Determine which horizontal drop zones to show based on current row state
  const getDropZones = () => {
    // In verticalOnly mode (quiz pages), don't show horizontal drop zones
    if (verticalOnly) return [];
    if (!isDragging || !isOver || !draggedModule) return [];

    const rowModules = pageRow.pageModules;
    const modulesCount = rowModules.length;

    // Row capacity rules:
    // - Max 3 modules per row
    // - If row already has 3 modules and the dragged module is NOT in this row,
    //   the row is full and cannot accept more modules.
    if (!draggedModuleInThisRow && modulesCount >= 3) {
      return [];
    }

    // If dragged module is the only one in this row, there's nothing to reorder
    // horizontally within this row.
    if (draggedModuleInThisRow && modulesCount === 1) {
      return [];
    }

    let baseZones = [];

    if (modulesCount === 0) {
      // Empty row: allow dropping in the center
      baseZones = [{ position: 'center', key: 'center' }];
    } else if (modulesCount === 1) {
      // 1 module in row
      // - Dragging from another row: show left + right
      // - Dragging from this row: no zones (handled above)
      if (!draggedModuleInThisRow) {
        baseZones = [
          { position: 'left', key: 'left' },
          { position: 'right', key: 'right' },
        ];
      }
    } else if (modulesCount === 2) {
      if (draggedModuleInThisRow) {
        // 2 modules in row, dragging one of them:
        // Show only the opposite side (swap with the other module).
        const firstModuleId = rowModules[0].id;
        const draggingFirst = draggedModule.id === firstModuleId;

        baseZones = [
          {
            position: draggingFirst ? 'right' : 'left',
            key: draggingFirst ? 'right' : 'left',
          },
        ];
      } else {
        // 2 modules in row, dragging from another row:
        // Show left + center + right (allow inserting a 3rd module).
        baseZones = [
          { position: 'left', key: 'left' },
          { position: 'center', key: 'center' },
          { position: 'right', key: 'right' },
        ];
      }
    } else if (modulesCount === 3 && draggedModuleInThisRow) {
      // 3 modules in row, dragging one of them:
      // Row is full, but internal reordering is allowed (show other positions).
      baseZones = [
        { position: 'left', key: 'left' },
        { position: 'center', key: 'center' },
        { position: 'right', key: 'right' },
      ];
    }

    if (baseZones.length === 0) {
      return [];
    }

    const originalOrderIds = rowModules.map((m) => m.id);
    const isSameRow = draggedModuleInThisRow;

    const simulateOrderForPosition = (position) => {
      const targetModules = [...rowModules];
      const draggedIndex = targetModules.findIndex((m) => m.id === draggedModule.id);

      if (draggedIndex !== -1) {
        targetModules.splice(draggedIndex, 1);
      }

      let insertIndex;
      if (position === 'left') {
        insertIndex = 0;
      } else if (position === 'center') {
        insertIndex = 1;
      } else {
        // 'right' - always insert at the end to preserve existing order
        insertIndex = targetModules.length;
      }

      const moduleToInsert = draggedIndex !== -1 ? rowModules[draggedIndex] : { id: draggedModule.id };

      const finalModules = [...targetModules];
      finalModules.splice(insertIndex, 0, moduleToInsert);

      return finalModules.map((m) => m.id);
    };

    const seenOrders = new Set();
    const validZones = [];

    baseZones.forEach((zone) => {
      const newOrder = simulateOrderForPosition(zone.position);

      if (!newOrder || newOrder.length === 0) {
        return;
      }

      const isSameOrder = isSameRow && newOrder.length === originalOrderIds.length && newOrder.every((id, idx) => id === originalOrderIds[idx]);

      // Skip no-op drops that would not change ordering for same-row drags
      if (isSameOrder) {
        return;
      }

      const orderKey = newOrder.join('|');
      if (seenOrders.has(orderKey)) {
        // Avoid duplicate zones that would result in the same final order
        return;
      }

      seenOrders.add(orderKey);
      validZones.push(zone);
    });

    return validZones;
  };

  const dropZones = getDropZones();
  const showDropZones = isDragging && dropZones.length > 0;

  // Render drop zones with modules interspersed
  const renderRowContent = () => {
    if (!showDropZones) {
      // Normal rendering when not dragging
      return (
        <RowModules
          $isNotesOpen={isNotesOpen}
          gap='20'
          width='100%'
          key={`pageRow-${pageRow.id}`}
          justify={pageModulesLength < 2 ? 'center' : 'space-between'}
          align='flex-start'
        >
          <ModuleContainer {...containerProps}>
            {pageRow.pageModules.map((module) =>
              renderModule(module, {
                ...moduleProps,
                $columns: pageModulesLength,
                key: `pageModules-${module.id}`,
              }),
            )}
          </ModuleContainer>
        </RowModules>
      );
    }

    // When dragging, show drop zones with proper spacing
    const hasLeftZone = dropZones.some((z) => z.position === 'left');
    const hasCenterZone = dropZones.some((z) => z.position === 'center');
    const hasRightZone = dropZones.some((z) => z.position === 'right');

    return (
      <DropZoneContainer $isActive={true} $justify={pageModulesLength === 0 ? 'center' : 'space-between'}>
        {hasLeftZone && <HorizontalDropZone position='left' rowId={pageRow.id} onDrop={onDrop} isVisible={true} themeColor={themeColor} />}

        {pageRow.pageModules.map((module, idx) => (
          <React.Fragment key={`module-${module.id}`}>
            {renderModule(module, {
              ...moduleProps,
              $columns: pageModulesLength,
              key: `pageModules-${module.id}`,
            })}
            {hasCenterZone && idx === 0 && pageModulesLength === 2 && (
              <HorizontalDropZone position='center' rowId={pageRow.id} onDrop={onDrop} isVisible={true} themeColor={themeColor} />
            )}
          </React.Fragment>
        ))}

        {hasRightZone && <HorizontalDropZone position='right' rowId={pageRow.id} onDrop={onDrop} isVisible={true} themeColor={themeColor} />}
      </DropZoneContainer>
    );
  };

  // Combine refs: both drop (for react-dnd) and rowRef (for measurements)
  const setRefs = React.useCallback(
    (node) => {
      rowRef.current = node;
      drop(node);
    },
    [drop],
  );

  return <RowWrapper ref={setRefs}>{renderRowContent()}</RowWrapper>;
};

export default DraggableRow;
